import apiService from './apiService';
import { ALL_LOCATIONS, locationKeyFor } from '../components/LocationSwitcher';
import { studioIdOf } from '../utils/studioOf';

/**
 * Private member notes — ONE per member per location, shared by all of its
 * staff (owner, managers, instructors). Kept in its own file, paths included,
 * like attendanceApi.
 *
 * studio/* — any studio staff. Reads return every note the caller may see (a
 *            brand owner: all their branches'); a write lands at the location
 *            it's pinned to (`atStudio`), never under "All".
 * admin/*  — super admins: every studio's notes on a member, edit / delete any.
 */
const BASE = '/api/v1/user-notes';

/** Matches the server's limit. */
export const MAX_NOTE_LENGTH = 5000;

export interface UserNote {
  _id: string;
  user: string;
  studio: string | { _id: string; studioName?: string; branchName?: string | null };
  text: string;
  /** Whoever last wrote it, and their role at that location. */
  authorName?: string;
  authorRole?: string;
  /** The viewer holds a role at the note's location (or is a super admin). */
  canEdit: boolean;
  updatedAt: string;
}

const ROLE_LABELS: Record<string, string> = {
  instructor: 'Instructor',
  manager: 'Manager',
  studio_owner: 'Owner',
  brand_owner: 'Brand owner',
  super_admin: 'Super admin',
};

export const noteStudioId = (note: UserNote) => studioIdOf(note.studio);

/** "Purple Pilates · Indiranagar" */
export const noteStudioLabel = (note: UserNote) => {
  const s = note.studio;
  if (!s || typeof s !== 'object') return 'Studio';
  return [s.studioName, s.branchName].filter(Boolean).join(' · ') || 'Studio';
};

/** "last edited by Rahul (Manager)" */
export const noteEditedByLabel = (note: UserNote) => {
  const role = note.authorRole ? ROLE_LABELS[note.authorRole] ?? '' : '';
  const name = note.authorName || role;
  if (!name) return '';
  return `last edited by ${role && name !== role ? `${name} (${role})` : name}`;
};

/**
 * Where a note written outside a booking lands: the location in the switcher,
 * or the login's own studio when there's no switcher. Null under "All
 * locations" — a note belongs to one location, so pick one first.
 */
export function currentNoteStudioId(): string | null {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(locationKeyFor());
  } catch {
    /* storage blocked — fall back to the login's studio */
  }
  if (stored === ALL_LOCATIONS) return null;
  return stored || apiService.ownStudioId();
}

export const userNotesApi = {
  forUsers: (userIds: string[]) =>
    apiService.get<UserNote[]>(`${BASE}/studio`, { params: { userIds: userIds.join(',') } }),

  adminForUsers: (userIds: string[]) =>
    apiService.get<UserNote[]>(`${BASE}/admin`, { params: { userIds: userIds.join(',') } }),

  /**
   * Save the member's note at `studioId` (creates or replaces). A super admin
   * edits an existing note by id instead — pass it as `existing`.
   */
  save: (userId: string, text: string, studioId: string, existing?: UserNote | null) =>
    apiService.isStudio()
      ? apiService.put<UserNote>(`${BASE}/studio/${userId}`, { text }, apiService.atStudio(studioId))
      : apiService.patch<UserNote>(`${BASE}/admin/notes/${existing?._id}`, { text }),

  remove: (note: UserNote) =>
    apiService.isStudio()
      ? apiService.delete(`${BASE}/studio/${note.user}`, apiService.atStudio(noteStudioId(note)))
      : apiService.delete(`${BASE}/admin/notes/${note._id}`),
};
